// CORE
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
// MUI
import { useTheme } from '@mui/material/styles';

// APP-COMPONENTS
import { actionHelpers } from 'app-components/flux/core';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getLogger, tracker } from 'app-components/utils/core';

// ASSETS
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import GreenThumbImage from 'assets/illustrations/Thumbs-up-star.png';

import SetupPage from 'components/Dialogs/IdentityVerification/SetupPage';
// Shared
import StdDialog from 'components/Dialogs/StdDialog';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';

// LOCAL
import VerifyEmailForIdentityVerification from './VerifyEmail';
import VerifyEmailSentForIdentityVerification from './VerifyEmailSent';
import { emailDialogIndexEnums } from './emailDialogIndexEnums';
import useStyles from './styles';

const logger = getLogger('components/Dialogs/IdentityVerification');

export const DIALOG_TYPE_EMAIL_VERIFICATION_DIALOG = 'DIALOG_TYPE_EMAIL_VERIFICATION_DIALOG';

/*  VIEW INDEXES MAP
  READY : For when selector data is ready to display
  FAILED : For when there was a failure retrieving selector data
  LOADING : For when selector data is loading
*/
function EmailVerificationDialog(props) {
  const isMounted = useRef();
  const theme = useTheme();

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { initiator, onConfirm, startTab } = props;

  // =============== SELECTORS =================
  const profile = useSelector(profileSelectors.getProfile);
  const emailVerified = useSelector(profileSelectors.getEmailVerified);

  // ==================== STATES ===================
  const [tabIndex, setTabIndex] = useState(startTab || emailDialogIndexEnums.OPENING);
  const [showCheckAgainButton, setShowCheckAgainButton] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  //  ================= VIEW STATE LOGIC =====================
  const profileView = useLoadState(profileSelectors);

  const mainView = useMemo(() => {
    const showSkeletonView =
      loadState.skeletonStates.includes(profileView) || tabIndex === emailDialogIndexEnums.LOADING;
    const showTryAgainView =
      (loadState.tryAgainStates.includes(profileView) || loadState.updateRequiredStates.includes(profileView)) &&
      tabIndex !== emailDialogIndexEnums.LOADING;
    const showNoDataView = loadState.noDataStates.includes(profileView) && tabIndex !== emailDialogIndexEnums.LOADING;
    const showReadyView = loadState.readyStates.includes(profileView) && tabIndex !== emailDialogIndexEnums.LOADING;

    //  Ordered by priority
    if (showReadyView || showNoDataView) {
      //  There will be no EMPTY view for this dialog
      return 'READY';
    } else if (showTryAgainView) {
      return 'FAILED';
    } else if (showSkeletonView) {
      return 'LOADING';
    }
  }, [profileView, tabIndex]);

  // ==================== CALLBACK FUNCTIONS =====================
  const setPreviousIndex = () => {
    if (tabIndex === emailDialogIndexEnums.PENDING) {
      setTabIndex(emailDialogIndexEnums.SUBMIT);
    } else if (tabIndex === emailDialogIndexEnums.SUBMIT) {
      setTabIndex(emailDialogIndexEnums.OPENING);
    }
  };

  const refreshData = useCallback(() => {
    dispatch(profileActions.getProfile());
  }, [dispatch]);

  const handleClose = useCallback(
    (_event) => {
      if (emailVerified && isMounted.current === true) {
        tracker.track(tracker.events.emailVerified);
      }
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch, emailVerified],
  );

  const handleEmailCheck = useCallback(async () => {
    dispatch(profileActions.getProfile());
  }, [dispatch]);

  const handleSendEmail = useCallback(
    async (isResent) => {
      actionHelpers
        .bindPromiseAction(dispatch, profileActions.getProfile)()
        .then((_resp) => {
          if (isMounted.current === true) {
            if (emailVerified) return;
            setTabIndex(emailDialogIndexEnums.PENDING);
            setIsSendingEmail(false);

            // Due to the constant polling in the saga, the action is not "finished" for a long time, so call mixpanel events now
            if (isResent) {
              tracker.track(tracker.events.verificationEmailResent);
            } else {
              tracker.track(tracker.events.verificationEmailSent);
            }

            actionHelpers
              .bindPromiseAction(dispatch, profileActions.verifyEmail)()
              .then((_resp) => {
                if (isMounted.current === true) {
                  handleEmailCheck();
                  setShowCheckAgainButton(true);
                }
                logger.log('Successfully sent email');
              })
              .catch((_error) => {
                if (isMounted.current === true) {
                  handleEmailCheck();
                  setShowCheckAgainButton(true);
                }
              });
          }
        })
        .catch((error) => assert(false, error));
    },
    [dispatch, emailVerified, handleEmailCheck],
  );

  const resendEmail = useCallback(() => {
    setIsSendingEmail(true);
    setTabIndex(emailDialogIndexEnums.LOADING);
    setTimeout(() => {
      handleSendEmail(true);
    }, 2500);
  }, [handleSendEmail]);

  const handleContinue = useCallback(
    (e) => {
      if (tabIndex === emailDialogIndexEnums.OPENING) {
        setTabIndex(emailDialogIndexEnums.SUBMIT);
      } else if (tabIndex === emailDialogIndexEnums.SUBMIT) {
        handleSendEmail();
      } else if (emailVerified) {
        handleClose(e, 'COMPLETE');
        onConfirm();
      }
    },
    [emailVerified, handleClose, handleSendEmail, onConfirm, tabIndex],
  );

  // =================== EFFECTS ===================

  // track mounted state because of async promise
  useEffect(() => {
    dispatch(profileActions.getProfile());
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  useEffect(() => {
    // Check if the email has been verified!
    if (emailVerified) {
      setTimeout(() => handleContinue(), 3000);
      setTabIndex(emailDialogIndexEnums.SUCCESS);
    }
  }, [emailVerified, handleContinue]);

  useEffect(() => {
    if (tabIndex === emailDialogIndexEnums.OPENING) {
      tracker.track(tracker.events.viewTransfersIntroScreen, { initiator: 'email' });
    } else if (tabIndex === emailDialogIndexEnums.SUBMIT) {
      tracker.track(tracker.events.viewEmailVerification);
    }
  }, [initiator, tabIndex]);

  // ================ DERIVED VALUES ====================

  let dialogTitle = 'Email verification';
  dialogTitle = tabIndex === emailDialogIndexEnums.OPENING ? 'Set up account transfers' : dialogTitle;
  dialogTitle = tabIndex === emailDialogIndexEnums.SUBMIT && !emailVerified ? 'Verify your email address' : dialogTitle;
  dialogTitle = tabIndex === emailDialogIndexEnums.PENDING && !emailVerified ? 'Verification email sent' : dialogTitle;
  dialogTitle = tabIndex === emailDialogIndexEnums.SUCCESS ? 'Email verified' : dialogTitle;

  let continueText = 'continue';
  continueText = tabIndex === emailDialogIndexEnums.OPENING ? 'get started' : continueText;
  continueText = tabIndex === emailDialogIndexEnums.SUBMIT && !emailVerified ? 'send email' : continueText;

  return (
    <StdDialog
      onBack={setPreviousIndex}
      showBackButton={
        mainView !== 'LOADING' &&
        (tabIndex === emailDialogIndexEnums.SUBMIT || tabIndex === emailDialogIndexEnums.PENDING)
      }
      disableBackdropClick
      onClose={handleClose}
      open
      sharedcomponentid={'EMAIL_VERIFICATION_DIALOG'}
      showCloseButton={tabIndex !== emailDialogIndexEnums.SUCCESS}
      size={'md'}
      title={dialogTitle}
    >
      <DialogContent>
        {tabIndex === emailDialogIndexEnums.OPENING && mainView === 'READY' && <SetupPage />}

        {tabIndex === emailDialogIndexEnums.SUBMIT && mainView === 'READY' && !emailVerified && (
          <VerifyEmailForIdentityVerification emailAddress={profile?.primaryEmail?.address} />
        )}

        {tabIndex === emailDialogIndexEnums.PENDING && mainView === 'READY' && !emailVerified && (
          <VerifyEmailSentForIdentityVerification sendVerifyEmail={resendEmail} />
        )}

        {tabIndex === emailDialogIndexEnums.SUCCESS && mainView === 'READY' && emailVerified && (
          <Box
            textAlign='center'
            pl={18}
            pr={18}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <img src={GreenThumbImage} alt='thumbs-up' className={classes.thumbsUp} />
            <br />
            <Typography variant='headlineRegular'>Email verified!</Typography>
          </Box>
        )}
        {(mainView === 'LOADING' || tabIndex === emailDialogIndexEnums.LOADING) && ( // Override mainView if tabIndex is LOADING
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
            {isSendingEmail && (
              <Typography fontWeight={500} variant='titleMedium'>
                Sending email...
              </Typography>
            )}
          </Box>
        )}
        {mainView === 'FAILED' && (
          <ZeroStateView
            className={classes.errorTab}
            button={
              <QButton id={'try-again-button'} variant='outlined' onClick={refreshData}>
                TRY AGAIN
              </QButton>
            }
            id='failed-to-fetch'
            primary='Unable to fetch data'
            secondary="We can't fetch your data from the server. Please try again"
            icon={paperAirplaneCrash}
          />
        )}
      </DialogContent>
      {mainView === 'READY' && tabIndex !== emailDialogIndexEnums.SUCCESS && (
        <DialogActions>
          {tabIndex === emailDialogIndexEnums.PENDING &&
            !emailVerified &&
            (showCheckAgainButton ? (
              <QButton
                variant='outlined'
                id='id-verification-email-check'
                aria-label='email-check'
                onClick={handleEmailCheck}
              >
                {'check again'}
              </QButton>
            ) : (
              <QButton
                width={100}
                disabled
                sx={{
                  '&.Mui-disabled': {
                    opacity: 1,
                    backgroundColor: `${theme.applyOpacityToHex?.(theme.palette.primary.main, 0.1)} !important`,
                  },
                }}
                variant='contained'
              >
                <CircularProgress size={20} className={classes.spinner} thickness={5} />
              </QButton>
            ))}
          {mainView === 'READY' &&
            !(tabIndex === emailDialogIndexEnums.PENDING) &&
            !emailVerified &&
            tabIndex !== emailDialogIndexEnums.LOADING && (
              <QButton variant='contained' id='id-verification-continue' aria-label='continue' onClick={handleContinue}>
                {continueText}
              </QButton>
            )}
        </DialogActions>
      )}
    </StdDialog>
  );
}

EmailVerificationDialog.propTypes = {
  initiator: PropTypes.string,
  onConfirm: PropTypes.func,
  startTab: PropTypes.number,
};

export default EmailVerificationDialog;
